import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { BrandColors, Property } from '@/types';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function parseUrlParams(): { reservationId: string; token: string | null } {
  const pathParts = window.location.pathname.split('/').filter(Boolean);

  // New format: /r/{id}/{token} — token in path avoids iOS Safari truncating at '?'
  if (pathParts[0] === 'r' && pathParts.length >= 3) {
    return { reservationId: pathParts[1], token: pathParts[2] };
  }

  // Query token (?t=) is load-bearing: the wildcard route in App.tsx reconstructs
  // /dashboard/{id}?t={token} after init, so removing this breaks ALL magic links,
  // not just legacy /r/{id}?t= ones.
  const reservationId = pathParts[pathParts.length - 1] ?? '';
  const token = new URLSearchParams(window.location.search).get('t');
  return { reservationId, token };
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function rgbChannels([r, g, b]: [number, number, number]): string {
  return `${r} ${g} ${b}`;
}

function lightenRgb([r, g, b]: [number, number, number], amount: number): [number, number, number] {
  return [
    Math.min(255, Math.round(r + (255 - r) * amount)),
    Math.min(255, Math.round(g + (255 - g) * amount)),
    Math.min(255, Math.round(b + (255 - b) * amount)),
  ];
}

function darkenRgb([r, g, b]: [number, number, number], amount: number): [number, number, number] {
  return [
    Math.round(r * (1 - amount)),
    Math.round(g * (1 - amount)),
    Math.round(b * (1 - amount)),
  ];
}

// Blend toward the dark-mode card surface (#12313B) for tints that sit on dark backgrounds
const DARK_SURFACE: [number, number, number] = [18, 49, 59];
function mixRgb([r, g, b]: [number, number, number], [r2, g2, b2]: [number, number, number], t: number): [number, number, number] {
  return [Math.round(r + (r2 - r) * t), Math.round(g + (g2 - g) * t), Math.round(b + (b2 - b) * t)];
}

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyBrandTheme(brandColors: Property['brandColors']): void {
  if (!brandColors) return;

  try {
    const colors: BrandColors = typeof brandColors === 'string'
      ? JSON.parse(brandColors)
      : brandColors;

    if (!colors.primary) return;

    const root = document.documentElement;
    const primary = hexToRgb(colors.primary);

    // RGB channel variables — used by Tailwind's rgb(var(--x) / <alpha-value>) color tokens
    root.style.setProperty('--velohost-primary-rgb',     rgbChannels(primary));
    // Tints: index.css picks the light or dark set depending on the colour scheme
    root.style.setProperty('--velohost-primary-50-light',  rgbChannels(lightenRgb(primary, 0.93)));
    root.style.setProperty('--velohost-primary-100-light', rgbChannels(lightenRgb(primary, 0.82)));
    root.style.setProperty('--velohost-primary-200-light', rgbChannels(lightenRgb(primary, 0.62)));
    root.style.setProperty('--velohost-primary-50-dark',   rgbChannels(mixRgb(primary, DARK_SURFACE, 0.82)));
    root.style.setProperty('--velohost-primary-100-dark',  rgbChannels(mixRgb(primary, DARK_SURFACE, 0.7)));
    root.style.setProperty('--velohost-primary-200-dark',  rgbChannels(mixRgb(primary, DARK_SURFACE, 0.5)));
    // Brand-coloured text on dark surfaces needs a lighter shade to stay readable
    root.style.setProperty('--velohost-primary-text-dark', rgbChannels(lightenRgb(primary, 0.5)));
    root.style.setProperty('--velohost-primary-600-rgb', rgbChannels(
      colors.primaryDark ? hexToRgb(colors.primaryDark) : darkenRgb(primary, 0.12)
    ));
    root.style.setProperty('--velohost-primary-700-rgb', rgbChannels(darkenRgb(primary, 0.27)));
    if (colors.secondary) {
      root.style.setProperty('--velohost-secondary-rgb', rgbChannels(hexToRgb(colors.secondary)));
    }

    // shadcn/ui system — drives `primary` / `ring` tokens used by Button etc.
    const hsl = hexToHsl(colors.primary);
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
  } catch (error) {
    console.error('Failed to apply brand theme:', error);
  }
}
